import { useCallback, useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { api } from "../../api/client";
import { theme, floatingSheet, countTag, emptyFrame, btnSecondary, inputStyle } from "../../theme";
import { DimensionHeading, StatusTag, type StatusTone } from "../../components/Drawing";
import { ControlStatusBadge, displayRef } from "../../components/controlStatus";
import { EvidenceSourceBadge } from "../../components/EvidenceImportPanel";
import { Spinner } from "../../components/Spinner";
import { fmtDate, fmtBytes } from "../../utils/format";
import { fmtDateTime } from "../../lib/format";
import {
  AUDIT_ACTION_LABELS, ORIGIN_LABELS, POLICY_STATE_LABELS,
  type AuditSnapshotSummary, type ControlTrail, type TrailDocument,
} from "./auditTools";

// The evidence trail behind one requirement (GET /auditor/clients/:orgId/controls/:id/trail):
// the client's SOA row, every evidence document with its versions, the adopted
// policy linked to the control, the latest AI verdict with the evidence it cited,
// and the SOA change history. "As at" re-reads it from an audit snapshot, so the
// auditor sees what was in force on the audit date rather than what is there now.
// Read-only: opening a document is the only action.

const VERDICT_TONE: Record<string, StatusTone> = { COMPLIANT: "met", PARTIAL: "partial", GAP: "gap", NOT_APPLICABLE: "none" };
const VERDICT_LABEL: Record<string, string> = { COMPLIANT: "Compliant", PARTIAL: "Partial", GAP: "Gap", NOT_APPLICABLE: "Not applicable" };

const sectionLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 8px",
};

function showValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "none";
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}

export function EvidenceTrailDrawer({ orgId, controlId, controlName, onClose }: {
  orgId: string;
  controlId: string;
  controlName: string;
  onClose: () => void;
}) {
  const [trail, setTrail] = useState<ControlTrail | null>(null);
  const [snapshots, setSnapshots] = useState<AuditSnapshotSummary[]>([]);
  const [snapshotId, setSnapshotId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ snapshots: AuditSnapshotSummary[] }>(`/auditor/clients/${orgId}/snapshots`)
      .then((d) => setSnapshots(d.snapshots))
      .catch(() => setSnapshots([]));
  }, [orgId]);

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get<ControlTrail>(`/auditor/clients/${orgId}/controls/${encodeURIComponent(controlId)}/trail`, snapshotId ? { snapshotId } : undefined)
      .then(setTrail)
      .catch(() => setError("Could not load the evidence trail."))
      .finally(() => setLoading(false));
  }, [orgId, controlId, snapshotId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const openDocument = useCallback(async (docId: string) => {
    setOpening(docId);
    try {
      const { url } = await api.get<{ url: string }>(`/auditor/clients/${orgId}/documents/${docId}/download-url`);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setError("That version could not be opened. It may have been removed, or it is outside your audit scope.");
    } finally {
      setOpening(null);
    }
  }, [orgId]);

  const groupName = (groupId: string) => trail?.documents.find((d) => d.documentGroupId === groupId)?.fileName ?? null;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1500 }} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Evidence trail for ${controlName}`}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, width: 560, maxWidth: "100vw",
          ...floatingSheet, borderRadius: 0, zIndex: 1501, display: "flex", flexDirection: "column",
        }}
      >
        {/* Ruled header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "16px 18px", borderBottom: `1px solid ${theme.color.line}`, flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: theme.color.muted, fontWeight: 700, letterSpacing: "0.05em" }}>EVIDENCE TRAIL · {displayRef(controlId)}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginTop: 2 }}>{controlName}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: theme.color.faint }}>
            <X size={16} />
          </button>
        </div>

        {/* As at */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderBottom: `1px solid ${theme.color.line}`, flexShrink: 0, flexWrap: "wrap" }}>
          <label htmlFor="trail-as-at" style={{ fontSize: 12, color: theme.color.muted, fontWeight: 600 }}>As at</label>
          <select
            id="trail-as-at"
            value={snapshotId}
            onChange={(e) => setSnapshotId(e.target.value)}
            style={{ ...inputStyle, width: "auto", flex: 1, minWidth: 200, padding: "5px 8px", fontSize: 12 }}
          >
            <option value="">Now (live)</option>
            {snapshots.map((s) => (
              <option key={s.id} value={s.id}>
                {fmtDateTime(s.takenAt)}{s.label ? ` · ${s.label}` : s.trigger === "schedule" ? " · scheduled audit date" : ""}
              </option>
            ))}
          </select>
          {trail?.asAt && <StatusTag tone="partial">Snapshot</StatusTag>}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 20 }}>
          {loading && <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Spinner /></div>}
          {error && <div role="alert" style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}

          {!loading && trail && (
            <>
              {trail.asAt && (
                <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>
                  What was in force when the snapshot was taken on {fmtDateTime(trail.asAt.takenAt)}
                  {trail.asAt.stageDate ? ` for the audit scheduled on ${fmtDate(trail.asAt.stageDate)}` : ""}. Later versions and changes are not shown.
                </div>
              )}

              {/* Statement of Applicability */}
              <section>
                <h3 style={sectionLabel}>Statement of Applicability</h3>
                {trail.soa ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <ControlStatusBadge status={trail.soa.status} />
                      <span style={{ fontSize: 12, color: theme.color.subtle }}>
                        Applicable: {trail.soa.applicable === null ? "undecided" : trail.soa.applicable ? "yes" : "no"}
                      </span>
                      {trail.soa.approvalStatus === "DRAFT" && <StatusTag tone="partial">Awaiting consultant approval</StatusTag>}
                    </div>
                    {trail.soa.justification && <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.5 }}>{trail.soa.justification}</div>}
                    {trail.soa.updatedAt && <div style={{ fontSize: 11, color: theme.color.faint }}>Last changed {fmtDateTime(trail.soa.updatedAt)}</div>}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: theme.color.faint }}>The client has not assessed this control.</div>
                )}
              </section>

              {/* Evidence documents */}
              <section>
                <DimensionHeading as="h3" count={trail.documents.length} style={{ fontSize: 13 }}>Evidence documents</DimensionHeading>
                {trail.documents.length === 0 ? (
                  <div style={{ ...emptyFrame, fontSize: 12, color: theme.color.faint }}>No document is filed under this control.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {trail.documents.map((doc) => (
                      <TrailDocumentCard key={doc.documentGroupId} doc={doc} opening={opening} onOpen={openDocument} />
                    ))}
                  </div>
                )}
              </section>

              {/* Adopted policy */}
              <section>
                <h3 style={sectionLabel}>Policy</h3>
                {trail.policies.length === 0 ? (
                  <div style={{ fontSize: 12, color: theme.color.faint }}>No policy template is linked to this control.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {trail.policies.map((p) => (
                      <div key={p.templateId} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
                        <span style={{ fontWeight: 600, color: theme.color.heading }}>{p.title}</span>
                        <StatusTag tone={p.state === "adopted" ? "met" : p.state === "adopted_superseded" ? "partial" : "none"}>
                          {POLICY_STATE_LABELS[p.state] ?? p.state}
                        </StatusTag>
                        {p.adoptedAt && <span style={{ color: theme.color.faint }}>{fmtDate(p.adoptedAt)}</span>}
                        {p.adoptedDocumentId && (
                          <button onClick={() => openDocument(p.adoptedDocumentId!)} style={{ ...btnSecondary, fontSize: 11, padding: "2px 8px" }}>
                            Open adopted copy
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* AI verdict */}
              <section>
                <h3 style={sectionLabel}>Latest AI verdict</h3>
                {!trail.verdict ? (
                  <div style={{ fontSize: 12, color: theme.color.faint }}>No compliance check has assessed this control{trail.asAt ? " by the snapshot date" : ""}.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <StatusTag tone={VERDICT_TONE[trail.verdict.status] ?? "none"}>{VERDICT_LABEL[trail.verdict.status] ?? trail.verdict.status}</StatusTag>
                      <span style={{ fontSize: 11, color: theme.color.faint }}>
                        {trail.verdict.carriedFrom ? `Unchanged since ${fmtDate(trail.verdict.carriedFrom)}` : trail.verdict.completedAt ? `Check of ${fmtDate(trail.verdict.completedAt)}` : ""}
                      </span>
                    </div>
                    {trail.verdict.policyConflict && (
                      <div style={{ borderLeft: `3px solid ${theme.color.danger}`, background: theme.color.dangerBg, padding: "8px 10px", fontSize: 12, color: theme.color.text, lineHeight: 1.5 }}>
                        <strong>Against the client's own policy:</strong> {trail.verdict.policyConflict}
                      </div>
                    )}
                    {trail.verdict.citations.length > 0 && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 4 }}>Evidence cited</div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: theme.color.text, lineHeight: 1.6 }}>
                          {trail.verdict.citations.map((c, i) => (
                            <li key={i}>
                              {c.text}
                              {c.documentGroupIds.length > 0 && (
                                <span style={{ color: theme.color.faint }}> (matches {c.documentGroupIds.map(groupName).filter(Boolean).join(", ")})</span>
                              )}
                            </li>
                          ))}
                        </ul>
                        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>Matched to documents by file name; an unmatched item is shown as the check wrote it.</div>
                      </div>
                    )}
                    {trail.verdict.gaps.length > 0 && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 4 }}>Gaps</div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: theme.color.text, lineHeight: 1.6 }}>
                          {trail.verdict.gaps.map((g, i) => <li key={i}>{g}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* SOA history */}
              <section>
                <DimensionHeading as="h3" count={trail.history.length} style={{ fontSize: 13 }}>Change history</DimensionHeading>
                {trail.history.length === 0 ? (
                  <div style={{ fontSize: 12, color: theme.color.faint }}>No recorded change to this control's SOA row{trail.asAt ? " before the snapshot" : ""}.</div>
                ) : (
                  <ol style={{ listStyle: "none", margin: 0, padding: 0, borderLeft: `1px solid ${theme.color.line}` }}>
                    {[...trail.history].reverse().map((h) => (
                      <li key={h.id} style={{ padding: "0 0 10px 12px", fontSize: 12 }}>
                        <div style={{ color: theme.color.heading, fontWeight: 600 }}>
                          {AUDIT_ACTION_LABELS[h.action] ?? h.action}
                          <span style={{ color: theme.color.faint, fontWeight: 400 }}> · {fmtDateTime(h.at)} · {h.actor ?? h.role}</span>
                        </div>
                        {h.changes.length > 0 ? (
                          <div style={{ color: theme.color.subtle, lineHeight: 1.5 }}>
                            {h.changes.map((c, i) => (
                              <div key={i}>{c.field}: {showValue(c.before)} → {showValue(c.after)}</div>
                            ))}
                          </div>
                        ) : h.detail ? (
                          <div style={{ color: theme.color.subtle }}>{h.detail}</div>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function TrailDocumentCard({ doc, opening, onOpen }: { doc: TrailDocument; opening: string | null; onOpen: (id: string) => void }) {
  const [showAll, setShowAll] = useState(false);
  const versions = showAll ? doc.versions : doc.versions.slice(0, 1);
  return (
    <div style={{ border: `1px solid ${theme.color.line}`, borderRadius: 3, padding: "8px 10px", background: theme.color.panel }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{doc.fileName}</span>
        <span style={{ ...countTag }}>{ORIGIN_LABELS[doc.origin]}</span>
        <EvidenceSourceBadge source={doc.source} webUrl={doc.sourceWebUrl} />
        {doc.policyTemplateId && <StatusTag tone="met">Adopted policy</StatusTag>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 6 }}>
        {versions.map((v, i) => (
          <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: theme.color.subtle }}>
            <span style={{ fontWeight: 600, color: i === 0 ? theme.color.text : theme.color.subtle }}>v{v.version}</span>
            <span>{fmtDateTime(v.importedAt ?? v.uploadedAt)}</span>
            <span>{fmtBytes(v.sizeBytes)}</span>
            {!v.taggedToControl && <span title="This version is not itself tagged to the control">untagged version</span>}
            <button
              onClick={() => onOpen(v.id)}
              disabled={opening === v.id}
              aria-label={`Open version ${v.version} of ${doc.fileName}`}
              style={{ marginLeft: "auto", background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "1px 7px", fontSize: 11, color: theme.color.accentText, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <Download size={11} /> {opening === v.id ? "Opening" : "Open"}
            </button>
          </div>
        ))}
      </div>
      {doc.versions.length > 1 && (
        <button onClick={() => setShowAll((s) => !s)} style={{ background: "none", border: "none", padding: 0, marginTop: 4, fontSize: 11, color: theme.color.accentText, cursor: "pointer" }}>
          {showAll ? "Show the latest version only" : `Show all ${doc.versions.length} versions`}
        </button>
      )}
    </div>
  );
}
